import React, { useState, useEffect, useMemo, } from 'react';
import { zipObject, keyBy, omit, range, inRange, get, last, } from 'lodash';
import { useHistory, useParams, useLocation, } from 'react-router';
import { useSessionStorage, } from 'react-use';
import { Nav, NavItem, } from 'reactstrap';
import { toast } from 'react-toastify';
import { format as formatDate } from 'date-fns';
import qs from 'qs';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import { periodOfFiscalYear, fiscalYearOfPeriod, fullPathWithParams } from '../../utils';
import { getDocumentData, } from '../../shared/firebase';
import { getRates, } from '../../shared/models/exchangeRate';
import { pcaCompanyFields, } from '../../shared/models/company';
import { pcaIntegrationFields, } from '../../shared/models/subsidiary';
import { pcaDataCenters, } from '../../shared/config';
import firebase, { functions } from '../../firebase';
import CompanyPage from './CompanyPage';
import HelpLink from '../HelpLink';
import ModelFormModal from '../modals/ModelFormModal';
import CompanySyncButton from '../CompanySyncButton';
import ProgressButton from '../ProgressButton';
import ModalButton from '../ModalButton';
import useCompanySelector from '../hooks/useCompanySelector';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useQueryParams from '../hooks/useQueryParams';
import QuerySelector from '../QuerySelector';

const { keys, entries, } = Object;
const auth = firebase.auth();
const db = firebase.firestore();
const companiesRef = db.collection('companies');
const getBugyoAuthUrl = functions.httpsCallable('getBugyoAuthUrl');
const getPcaAuthUrl = functions.httpsCallable('getPcaAuthUrl');
const tabs = [
  { name: 'settings', label: '設定', path: '/settings', isActive: _ => _.pathname.match(/settings/) },
  { name: 'accountItemMappings', label: '科目マッピング', path: '/accountItemMappings', isActive: _ => _.pathname.match(/accountItemMappings/) },
  { name: 'sectionMappings', label: '部門マッピング', path: '/sectionMappings', isActive: _ => _.pathname.match(/sectionMappings/) },
  { name: 'individualTrials', label: '試算表', path: '/individualTrials', isActive: _ => _.pathname.match(/individualTrials/) },
  { name: 'sectionTrials', label: '部門別試算表', path: '/sectionTrials', isActive: _ => _.pathname.match(/sectionTrials/), filter: _ => !_.disabledSectionTrials },
  { name: 'individualAdjustments', label: '個別修正', path: '/individualAdjustments', isActive: _ => _.pathname.match(/individualAdjustments/) },
  { name: 'individualSs', label: '個別SS', path: '/individualSs', isActive: _ => _.pathname.match(/individualSs/) },
  { name: 'individualAdjusted', label: '個別修正・換算後', path: '/individualAdjusted', isActive: _ => _.pathname.match(/individualAdjusted/) },
  { name: 'exchangeDiffs', label: '為替換算差額', path: '/exchangeDiffs', isActive: _ => _.pathname.match(/exchangeDiffs/), filter: _ => (_.currency || 'jpy') !== 'jpy', },
  { name: 'relatedCompanyBalances', label: '関係会社債権債務・取引高', path: '/relatedCompanyBalances', isActive: _ => _.pathname.match(/relatedCompanyBalances/) },
  { name: 'cashes', label: '現金及び現金同等物', path: '/cashes', isActive: _ => _.pathname.match(/cashes/) },
  { name: 'fixedAssets', label: '固定資産', path: '/fixedAssets', isActive: _ => _.pathname.match(/fixedAssets/) },
  { name: 'investments', label: '投資関連', path: '/investments', isActive: _ => _.pathname.match(/investments/) },
  { name: 'finances', label: '財務関連', path: '/finances', isActive: _ => _.pathname.match(/finances/) },
  { name: 'miscellaneousAccounts', label: '雑勘定', path: '/miscellaneousAccounts', isActive: _ => _.pathname.match(/miscellaneousAccounts/) },
  { name: 'allowances', label: '引当金', path: '/allowances', isActive: _ => _.pathname.match(/allowances/) },
];


export default function RelatedCompanyPageHOC(WrappedComponent) {
  return CompanyPage(function RelatedCompanyPage (props) {
    const { yearMonths, prevEndYearMonth, period, yearMonth, company, user, location, history, match: { params: { companyId, relatedCompanyId } }, } = props;
    const { role, enabledAllRelatedCompanies, enabledRelatedCompanyIds, } = company?.users?.[user.id] || {};
    const canAccess = (user?.admin || ['owner', 'admin'].includes(role)) || enabledAllRelatedCompanies || enabledRelatedCompanyIds?.includes(relatedCompanyId);
    const isParent = company.id === relatedCompanyId;
    const subsidiaryId = isParent ? null : relatedCompanyId;
    const queryParams = useQueryParams();
    const [bugyoOauthSession, setBugyoOauthSession] = useState(null);
    const [bugyoIntegrationPath, setBugyoIntegrationPath] = useSessionStorage('bugyoIntegrationPath');
    const subsidiaries = useCollectionSubscription(company.ref.collection('subsidiaries').orderBy('index'), [company]);
    const relatedCompany = isParent ? company : subsidiaries.find(_ => _.id === relatedCompanyId);
    const isCurrentPeriodJoin = relatedCompany?.joinPeriod === period && yearMonth >= relatedCompany?.joinYearMonth;
    const joinYearMonth = relatedCompany?.isStartJoin ? (parseInt(relatedCompany?.joinYearMonth, 10) - 1).toString() : relatedCompany?.joinYearMonth;
    const startYearMonth = useMemo(_ => (
      isCurrentPeriodJoin ? (
        relatedCompany?.isStartJoin && period === relatedCompany?.joinPeriod ? (
          prevEndYearMonth
        ) : (
          formatDate(yearMonths.find(_ => formatDate(_, 'yyyyMM') === joinYearMonth), 'yyyyMM')
        )
      ) : prevEndYearMonth
    ), [relatedCompany, isCurrentPeriodJoin && yearMonths, !isCurrentPeriodJoin && prevEndYearMonth]);
    const prevExchangeRate = useDocumentSubscription(prevEndYearMonth && company.ref.collection('exchangeRates').doc(prevEndYearMonth), [prevEndYearMonth]);
    const exchangeRate = useDocumentSubscription(company.ref.collection('exchangeRates').doc(yearMonth), [yearMonth]);
    const { cr: prevCr } = getRates(relatedCompany?.currency, prevExchangeRate);
    const { ar, cr } = getRates(relatedCompany?.currency, exchangeRate);
    const relatedCompanies = [company, ...subsidiaries];
    const companyOptions = (
      ((user?.admin || ['owner', 'admin'].includes(role)) || enabledAllRelatedCompanies) ? relatedCompanies : relatedCompanies.filter(_ => enabledRelatedCompanyIds?.includes(_.id))
    ).map(_ => ({ label: _.display_name || _.name, value: _.id }));
    const onSelectCompany = async (selected) => {
      history.push(`${location.pathname.replace(/relatedCompanies\/[^/]+/, `relatedCompanies/${selected.value}`)}${location.search}`);
    };
    const isPkgLocked = !!relatedCompany?.pkgLocks?.[yearMonth];
    const reintegrateWithBugyo = async () => {
      setBugyoIntegrationPath(location.pathname + location.search);
      const { data: url } = await getBugyoAuthUrl();
      window.location.href = url;
    }
    useEffect(() => {
      (async () => {
        if(queryParams.bugyo === '1' && relatedCompany != null) {
          const { sessionId } = queryParams;
          if(!sessionId) return;
          try {
            const oauthSession = await getDocumentData(db.collection('oauthSessions').doc(sessionId));
            await oauthSession.ref.delete();
            const { companies: bugyoCompanies } = oauthSession;
            if(bugyoCompanies.some(_ => _.id === relatedCompany.sourceId)) {
              await relatedCompany.ref.update({
                bugyo: {
                  accessToken: oauthSession.encryptedAccessToken,
                  refreshToken: oauthSession.encryptedRefreshToken,
                  tokenType: oauthSession.tokenType,
                  expires: oauthSession.expires,
                },
              });
              toast.success('再認証しました');
              history.replace(location.pathname + '?' + qs.stringify(omit(queryParams, ['bugyo', 'sessionId'])));
            } else if(relatedCompany.sourceId == null) {
              setBugyoOauthSession(oauthSession);
            } else {
              toast.error('再認証できませんでした');
            }
          } catch (error) {
            toast.error('セッションの取得に失敗しました');
            history.replace(location.pathname);
          }
        }
      })();
    }, [relatedCompany != null]);

    return relatedCompany != null && (
      canAccess ? (
        <div className="related-company-page h-100 my-4 container-fluid">
          <div className="px-4 bg-white">
            {
              props.translate(
                <div>
                  <div className="d-flex justify-content-end mb-3">
                    <HelpLink text="個社別の連結PKGを作成する" />
                  </div>
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <div style={{ width: 400, zIndex: 6, }} className="position-relative">
                      <Select
                        value={companyOptions.find(_ => _.value === relatedCompanyId) || null}
                        options={companyOptions}
                        onChange={onSelectCompany}
                        className="w-100"
                      />
                    </div>
                    {
                      (isParent || relatedCompany.externalType !== null) && (
                        <CompanySyncButton className="ml-1" company={company} companySourceId={relatedCompany.sourceId} subsidiaryId={subsidiaryId} relatedCompany={relatedCompany} />
                      )
                    }
                    {
                      relatedCompany.externalType === 'bugyo' && (
                        <ProgressButton className="ml-1" color="secondary" process={reintegrateWithBugyo}>
                          再認証
                        </ProgressButton>
                      )
                    }
                  </div>
                  <Nav tabs className="mt-4 mb-4">
                    {
                      tabs.filter(_ => !_.filter || _.filter(relatedCompany)).map(({ label, name, isActive, path, }) => {
                        return (
                          <NavItem key={name}>
                            <Link className={classnames('nav-link cursor-pointer', { active: isActive(location) })} to={`/companies/${companyId}/relatedCompanies/${relatedCompanyId}${path}${location.search}`}>
                              {label}
                            </Link>
                          </NavItem>
                        )
                      })
                    }
                  </Nav>
                </div>
              )
            }
            <WrappedComponent {...props} {...{ relatedCompany, relatedCompanies, isParent, subsidiaryId, prevCr, ar, cr, prevExchangeRate, exchangeRate, isLockedMonth: props.isLockedMonth || isPkgLocked, startYearMonth, } } />
          </div>
          {
            bugyoOauthSession && (
              <ModelFormModal
                title="法人選択"
                isOpen
                onSubmit={async (values) => {
                  await relatedCompany.ref.update({
                    ...values,
                    bugyo: {
                      accessToken: bugyoOauthSession.encryptedAccessToken,
                      refreshToken: bugyoOauthSession.encryptedRefreshToken,
                      tokenType: bugyoOauthSession.tokenType,
                      expires: bugyoOauthSession.expires,
                    },
                  });
                  toast.success('再認証しました');
                  history.replace(location.pathname);
                  setBugyoOauthSession(null);
                }}
                fields={{
                  sourceId: {
                    label: '事業所',
                    type: 'select',
                    options: bugyoOauthSession.companies.map(_ => ({ label: _.display_name || _.name, value: _.id })),
                    validations: {
                      required: v => v != null,
                    },
                  },
                }}
                onClickClose={_ => {
                  history.replace(location.pathname);
                  setBugyoOauthSession(null);
                }}
              />
            )
          }
        </div>
      ) : (
        <div className="container">
          <div className="alert alert-danger mt-3">権限がありません</div>
        </div>
      )
    );
  });
};
